<template>
    <DAlert
        v-if="showNotification"
        color="warning"
        class="d-flex align-items-center"
    >
        <DIcon
            name="alert"
            :size="24"
        />
        <div class="pr-2">
            {{ __('customers.suspension.warning') }}
        </div>
    </DAlert>
    <DForm
        class="contract-form"
        @submit.prevent="submitForm"
    >
        <FormBreadcrumbs
            class="mt-2"
            :breadcrumbs="breadcrumbs"
            :active-breadcrumb="activeBreadcrum"
            @breadcrumb-clicked="changeBreadcrumb"
        />

        <!-- Basic information -->
        <div v-show="activeBreadcrum === 'subject'">
            <div class="mb-2">
                <h2 class="h4 mb-0">
                    {{ __('customers.suspension.about_hosting_package') }}
                </h2>
            </div>
            <DFormRow
                :label="__('customers.suspension.form.hosting_package')"
                :error="errors.hosting_package"
                :is-required="true"
            >
                <DInput
                    id="hosting_package"
                    v-model="form.hosting_package"
                    maxlength="255"
                    requried
                />
            </DFormRow>
            <DFormRow
                :label="__('customers.suspension.form.domain_name')"
                :error="errors.domain_name"
                :is-required="true"
            >
                <DInput
                    id="domain_name"
                    v-model="form.domain_name"
                    maxlength="255"
                    requried
                />
            </DFormRow>
            <DFormRow
                :label="__('customers.suspension.form.email')"
                :error="errors.email"
            >
                <DInput
                    id="email"
                    v-model="form.email"
                    maxlength="128"
                    requried
                />
            </DFormRow>
            <DFormRow
                :label="__('customers.suspension.form.suspension_type')"
                :error="errors.suspension_type"
                :is-required="true"
            >
                <DSelect
                    :id="`suspension-type-option`"
                    v-model="form.suspension_type"
                    :options="props.suspensionSubjectType"
                    add-input-classes="bg-light"
                    @update:modelValue="updateType"
                />
            </DFormRow>
        </div>

        <div v-show="activeBreadcrum === 'details'">
            <div class="mb-2">
                <h2 class="h4 mb-0">
                    {{ __('customers.suspension.about_suspension') }}
                </h2>
            </div>
            <DFormRow
                :label="__('customers.suspension.form.evidence')"
                :error="errors.evidence"
            >
                <DFormTextarea
                    v-model="form.evidence"
                    rows="8"
                />
            </DFormRow>
            <hr>
            <div class="mb-2">
                <h2 class="h4 mb-0">
                    {{ __('customers.suspension.email') }}
                </h2>
            </div>
            <DFormRow
                :label="__('customers.suspension.form.send_email')"
                :error="errors.send_email"
                :hint="customerHasEmail === false ? __('customers.suspension.form.missing_email') : ''"
            >
                <DSwitch
                    id="send_email"
                    v-model="form.send_email"
                    color="success"
                    variant="3d"
                    inline
                    :disabled="customerHasEmail === false"
                    @update:checked="updateSendEmailToggleButton"
                />
            </DFormRow>
            <DFormRow
                v-if="form.send_email === true"
                :label="__('customers.suspension.form.email_subject')"
                :error="errors.email_subject"
            >
                <DInput
                    id="email_subject"
                    v-model="form.email_subject"
                    maxlength="128"
                    requried
                />
            </DFormRow>
            <DFormRow
                v-if="form.send_email === true"
                :is-required="true"
                :label="__('customers.suspension.form.email_body')"
                :error="errors.email_body"
            >
                <DFormTextarea
                    v-model="form.email_body"
                    rows="8"
                />
            </DFormRow>
            <hr>
            <div class="mb-2">
                <h2 class="h4 mb-0">
                    {{ __('customers.suspension.sms') }}
                </h2>
            </div>
            <DFormRow
                :label="__('customers.suspension.form.send_sms')"
                :error="errors.send_sms"
                :hint="customerHasPhoneNumber === false ? __('customers.suspension.form.missing_phone_number') : ''"
            >
                <DSwitch
                    id="send_sms"
                    v-model="form.send_sms"
                    color="success"
                    variant="3d"
                    inline
                    :disabled="customerHasPhoneNumber === false"
                    @update:checked="updateSendSmsToggleButton"
                />
            </DFormRow>
            <DFormRow
                v-if="form.send_sms === true"
                :label="__('customers.suspension.form.sms_body')"
                :error="errors.sms_body"
            >
                <DInput
                    id="sms_body"
                    v-model="form.sms_body"
                    maxlength="320"
                    requried
                />
            </DFormRow>
        </div>

        <DFormControls
            :cancel-route="cancelRoute"
            :dirty="form.isDirty"
            :show-change-reason-input="false"
            :submit-message="__('customers.suspension.send')"
            @submit="submitForm"
        >
            <template #additional_button_before_submit>
                <DFab
                    v-if="backButtonAvailable"
                    id="btn-back"
                    icon="arrow-left"
                    :label="__('messages.back')"
                    @click="changeToPreviousBreadcrumb"
                />
                <DFab
                    v-if="nextButtonAvailable"
                    id="btn-next"
                    icon="arrow-right"
                    icon-position="after"
                    :label="__('messages.next')"
                    @click="changeToNextBreadcrumb"
                />
            </template>
        </DFormControls>
    </DForm>
</template>

<script setup>

import FormBreadcrumbs from '~@/Components/Customers/Contracts/FormBreadcrumbs.vue'
import { useForm } from '@inertiajs/vue3'
import { computed, ref, watch, onMounted } from 'vue'
import { __ } from '~@/Composables/translation'

import { route } from '~@/Composables/route'

const fieldsBySection = {
    subject: [
        'hosting_package',
        'domain_name',
        'email',
        'suspension_type'
    ],
    details: [
        'evidence',
        'send_email',
        'email_subject',
        'email_body',
        'send_sms',
        'sms_body'
    ]
}

const props = defineProps({
    errors: {
        type: Object,
        default: () => { return {} }
    },
    customer: {
        type: Object,
        default: () => { return {} }
    },
    contract: {
        type: Object,
        default: () => { return {} }
    },
    suspensionSubjectType: {
        type: Array,
        default: () => { return [] }
    }
})

const hostingPackage = computed(() => `${props.contract.options.username.value ?? ''} (${props.contract.options.domain.value ?? ''})`)
const cancelRoute = computed(() => route('customers.customers.contracts.show', { customer: props.customer.id, contract: props.contract.id }))

const backButtonAvailable = computed(() => activeBreadcrum.value !== 'subject')
const nextButtonAvailable = computed(() => activeBreadcrum.value !== 'details')
const showNotification = ref(false)
const customerHasEmail = props.customer.email !== null
const customerHasPhoneNumber = props.customer.phone !== null || props.customer.mobilenumber !== null

const form = useForm({
    // Subject
    hosting_package: hostingPackage.value,
    domain_name: props.contract.options.domain.value && props.contract.options.domain.value,
    email: props.customer.email && props.customer.email,
    suspension_type: '',
    // Details
    evidence: '',
    send_email: customerHasEmail,
    email_subject: '',
    email_body: '',
    send_sms: customerHasPhoneNumber,
    sms_body: ''
})

const activeBreadcrum = ref('subject')

let breadcrumbs = [
    {
        key: 'subject',
        text: __('customers.contracts.subject'),
        hasError: false,
        isShown: true
    },
    {
        key: 'details',
        text: __('customers.contracts.details'),
        hasError: false,
        isShown: true
    }
]

const emit = defineEmits(['submit'])
const submitForm = () => emit('submit', form)

const setBreadCrumbsErrors = () => {
    const breadCrumbs = []

    for (const breadcrumb of breadcrumbs) {
        breadCrumbs.push({
            ...breadcrumb,
            hasError: breadcrumbHasErrors(breadcrumb)
        })
    }

    breadcrumbs = breadCrumbs
}

const breadcrumbHasErrors = (breadcrumb) => {
    for (const filedWithError of Object.keys(props.errors)) {
        for (const fieldInBreadcrumbSection of fieldsBySection[breadcrumb.key]) {
            if (filedWithError.startsWith(fieldInBreadcrumbSection)) {
                return true
            }
        }
    }

    return false
}

const setDefaultState = () => {
    form.evidence = ''
    form.send_email = false
    form.email_subject = ''
    form.email_body = ''
    form.send_sms = false
    form.sms_body = ''
}

const changeBreadcrumb = (breadcrumb) => {
    if (breadcrumb === 'details') {
        detailsBreadCrumb()
    }
    if (breadcrumb === 'subject') {
        subjectBreadCrumb()
    }
    activeBreadcrum.value = breadcrumb
}
const changeToPreviousBreadcrumb = () => {
    subjectBreadCrumb()
    for (const i in breadcrumbs) {
        if (activeBreadcrum.value === breadcrumbs[i].key) {
            changeBreadcrumb(breadcrumbs[Number(i) - 1].key)
            return
        }
    }
}

const changeToNextBreadcrumb = () => {
    detailsBreadCrumb()
    for (const i in breadcrumbs) {
        if (activeBreadcrum.value === breadcrumbs[i].key) {
            changeBreadcrumb(breadcrumbs[Number(i) + 1].key)

            return
        }
    }
}

const detailsBreadCrumb = () => {
    getEmailTemplate()
    changeNotificationToFalse()
}

const subjectBreadCrumb = () => {
    setDefaultState()
    changeNotificationToTrue()
}
watch(() => props.errors, (newValue) => {
    setBreadCrumbsErrors(newValue)
}, { deep: true, immediate: true })

onMounted(() => {
    setBreadCrumbsErrors(props.errors)
})

const updateSendEmailToggleButton = (value) => {
    form.send_email = value
}

const updateSendSmsToggleButton = (value) => {
    form.send_sms = value
}

const updateType = (value) => {
    form.type = value
}

const getEmailTemplate = async () => {
    let response = await window.axios.post(route('customers.customers.get-suspension-templates', {
        customer: props.customer.id,
        contract: props.contract.id
    }), {
        suspension_type: form.suspension_type,
        hosting_package: form.hosting_package,
        domain_name: form.domain_name,
        email: form.email
    })
    response = response.data

    form.email_body = response.emailTemplate
    form.email_subject = response.emailSubject
    form.sms_body = response.smsTemplate
}

const changeNotificationToTrue = () => {
    showNotification.value = true
}

const changeNotificationToFalse = () => {
    showNotification.value = false
}

</script>

<style scoped lang="scss">
.contract-form {
    display: grid;
    grid-gap: 15px;
}

hr {
    width: 100%;
    margin-bottom: 31px !important;
}

.row-margin {
    margin-bottom: 25px !important;
}

.pricing-and-runtimes-padding {
    padding-bottom: 10px;
}

.basic-information-margin {
    margin-bottom: -16px !important;
}
</style>
